import jwtDecode from 'jwt-decode';
import { APP_ROUTES } from 'shared/constants/navigation';
import { StorageService, StorageKeys } from 'lib/StorageService';

function hasBusinessDetails(webstore) {
    const addresses = webstore?.Addresses;
    const hasAddressesArray = Array.isArray(addresses);
    const addressesLength = hasAddressesArray ? addresses.length : 0;

    console.log('[VendorConnectRedirect] business-details-check', {
        hasAddressesArray,
        addressesLength,
        addressesPreview: hasAddressesArray
            ? addresses.slice(0, 3).map((address, index) => ({
                  index,
                  addressType: address?.addressType || null,
                  hasAddress1: Boolean(`${address?.address1 || ''}`.trim()),
                  hasCity: Boolean(`${address?.city || ''}`.trim()),
                  hasState: Boolean(`${address?.state || ''}`.trim()),
                  hasZipCode: Boolean(`${address?.zipCode || ''}`.trim()),
              }))
            : [],
    });

    return hasAddressesArray && addressesLength > 0;
}

function setTokenCookie(name, value) {
    if (!value) {
        console.log('[VendorConnectRedirect] cookie-skip-no-value', { name });
        return;
    }
    const encodedValue = encodeURIComponent(value);
    const maxAgeSeconds = 60 * 60 * 24;
    document.cookie = `${name}=${encodedValue}; Max-Age=${maxAgeSeconds}; Path=/; Domain=.davidsbridal.com; Secure; SameSite=Lax`;
    console.log('[VendorConnectRedirect] cookie-set', {
        name,
        valueLength: String(value).length,
    });
}

function redirectToVendorConnectOrFallback(tokenService, navigate, path = '') {
    const baseUrl = process.env.REACT_APP_VENDORCONNECT_URL;
    const accessToken = tokenService?.getAccessToken?.();
    const refreshToken = tokenService?.getRefreshToken?.();
    const idToken = tokenService?.getIdToken?.();

    console.log('[VendorConnectRedirect] redirect-inputs', {
        baseUrl,
        path,
        hasBaseUrl: Boolean(baseUrl),
        hasAccessToken: Boolean(accessToken),
        accessTokenLength: accessToken ? accessToken.length : 0,
        hasRefreshToken: Boolean(refreshToken),
        refreshTokenLength: refreshToken ? refreshToken.length : 0,
        hasIdToken: Boolean(idToken),
        idTokenLength: idToken ? idToken.length : 0,
    });

    if (!baseUrl || !accessToken || !refreshToken || !idToken) {
        console.log('[VendorConnectRedirect] fallback-dashboard', {
            reason: 'missing-env-or-token',
            hasBaseUrl: Boolean(baseUrl),
            hasAccessToken: Boolean(accessToken),
            hasRefreshToken: Boolean(refreshToken),
            hasIdToken: Boolean(idToken),
            route: APP_ROUTES.vendor.account.information,
        });
        navigate(APP_ROUTES.vendor.account.information);
        return;
    }

    setTokenCookie('PEARL_VENDORCONNECT_ACCESS_TOKEN', accessToken);
    setTokenCookie('PEARL_VENDORCONNECT_REFRESH_TOKEN', refreshToken);
    setTokenCookie('PEARL_VENDORCONNECT_ID_TOKEN', idToken);

    const redirectUrl = path ? `${baseUrl}${path}` : baseUrl;
    console.log('[VendorConnectRedirect] redirecting', { target: redirectUrl });
    window.location.assign(redirectUrl);
}

const VENDORCONNECT_COOKIE_NAMES = {
    accessToken: 'VENDORCONNECT_PEARL_ACCESS_TOKEN',
    refreshToken: 'VENDORCONNECT_PEARL_REFRESH_TOKEN',
    idToken: 'VENDORCONNECT_PEARL_ID_TOKEN',
};

function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function clearCookie(name) {
    const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    document.cookie = expired;
    document.cookie = `${expired}; Domain=.davidsbridal.com`;
}

function clearVendorConnectSessionCookies() {
    Object.values(VENDORCONNECT_COOKIE_NAMES).forEach(clearCookie);
}

/**
 * Extract email from the ID token stored in cookies
 * The ID token is set by Vendor Connect and contains user claims including email
 */
function getEmailFromVendorConnectToken() {
    try {
        const idToken = getCookie('VENDORCONNECT_PEARL_ID_TOKEN');
        if (!idToken) {
            console.log('[VendorConnectRedirect] no-idtoken-for-email');
            return null;
        }

        const decoded = jwtDecode(idToken);
        const email = decoded.email || decoded.preferred_username || decoded.sub;

        if (email) {
            console.log('[VendorConnectRedirect] email-extracted-from-token', {
                emailLength: String(email).length,
                hasEmail: Boolean(email),
            });
            return email;
        }

        console.log('[VendorConnectRedirect] no-email-claim-in-token', {
            tokenClaims: Object.keys(decoded),
        });
        return null;
    } catch (error) {
        console.error('[VendorConnectRedirect] error-decoding-token', error);
        return null;
    }
}

// Mirrors redirectToVendorConnectOrFallback, but in reverse: if Pearl has no
// session of its own, pick up a still-valid one that Connect left behind in
// the shared-domain cookies.
function hydrateFromVendorConnectSession(tokenService) {
    const accessToken = getCookie(VENDORCONNECT_COOKIE_NAMES.accessToken);
    const refreshToken = getCookie(VENDORCONNECT_COOKIE_NAMES.refreshToken);
    const idToken = getCookie(VENDORCONNECT_COOKIE_NAMES.idToken);

    if (!accessToken || !refreshToken || !idToken) {
        return false;
    }

    try {
        const { exp } = jwtDecode(accessToken);
        if (!exp || Date.now() >= exp * 1000) {
            clearVendorConnectSessionCookies();
            return false;
        }
    } catch {
        clearVendorConnectSessionCookies();
        return false;
    }

    tokenService.setAccessToken(accessToken);
    tokenService.setIdToken(idToken);
    tokenService.setRefreshToken(refreshToken);
    // This session originated from Connect's vendor auth, so refreshes need to
    // go through the same vendor-auth path as a native Pearl vendor login.
    StorageService().set(StorageKeys.newVendor, 'newVendor');

    clearVendorConnectSessionCookies();

    return true;
}

export {
    hasBusinessDetails,
    redirectToVendorConnectOrFallback,
    hydrateFromVendorConnectSession,
    getEmailFromVendorConnectToken,
};
